import * as React from 'react';
import { Box, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import {
  captureMapAsDataUrl,
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  geocodeAddress,
  GoogleMapState,
  LatLng,
  loadGoogleMapsApi,
  MAP_SIZE,
} from '../../utils/googleMaps';

export type GoogleMapHandle = {
  getState: () => GoogleMapState | null;
  captureAsDataUrl: () => Promise<string>;
  goToAddress: (address: string) => Promise<boolean>;
};

type GoogleMapProps = {
  address?: string;
  /** Cambia al cambio sito per forzare un nuovo posizionamento. */
  resetKey?: string;
};

function syncStateFromMap(
  map: google.maps.Map,
  marker: google.maps.Marker,
  target: React.MutableRefObject<GoogleMapState | null>
) {
  const center = map.getCenter();
  const markerPos = marker.getPosition();
  if (!center || !markerPos) return;

  target.current = {
    center: { lat: center.lat(), lng: center.lng() },
    zoom: map.getZoom() ?? DEFAULT_ZOOM,
    mapTypeId: map.getMapTypeId() ?? 'hybrid',
    markerPosition: { lat: markerPos.lat(), lng: markerPos.lng() },
  };
}

function applyPosition(
  map: google.maps.Map,
  marker: google.maps.Marker,
  position: LatLng,
  stateRef: React.MutableRefObject<GoogleMapState | null>
) {
  marker.setPosition(position);
  map.setCenter(position);
  map.setZoom(DEFAULT_ZOOM);
  google.maps.event.trigger(map, 'resize');
  syncStateFromMap(map, marker, stateRef);
}

const GoogleMap = React.forwardRef<GoogleMapHandle, GoogleMapProps>(({ address, resetKey }, ref) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<google.maps.Map | null>(null);
  const markerRef = React.useRef<google.maps.Marker | null>(null);
  const stateRef = React.useRef<GoogleMapState | null>(null);
  const lastAppliedAddressRef = React.useRef('');
  const pendingAddressRef = React.useRef<string | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [isReady, setIsReady] = React.useState(false);

  React.useEffect(() => {
    lastAppliedAddressRef.current = '';
    pendingAddressRef.current = null;
  }, [resetKey]);

  const goToAddressImpl = React.useCallback(async (addr: string, showError = true): Promise<boolean> => {
    const trimmed = addr.trim();
    if (!trimmed) return false;

    if (trimmed === lastAppliedAddressRef.current) return true;

    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) {
      pendingAddressRef.current = trimmed;
      return false;
    }

    const position = await geocodeAddress(trimmed);
    if (!position) {
      if (showError) {
        toast.error('Impossibile trovare le coordinate per questo indirizzo');
      }
      return false;
    }

    applyPosition(map, marker, position, stateRef);
    lastAppliedAddressRef.current = trimmed;
    pendingAddressRef.current = null;
    return true;
  }, []);

  React.useImperativeHandle(
    ref,
    () => ({
      getState: () => stateRef.current,
      captureAsDataUrl: async () => {
        const map = mapRef.current;
        const container = containerRef.current;
        if (!map || !container || !isReady) {
          throw new Error('Mappa non pronta');
        }
        return captureMapAsDataUrl(map, container);
      },
      goToAddress: (addr: string) => goToAddressImpl(addr, true),
    }),
    [isReady, goToAddressImpl]
  );

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await loadGoogleMapsApi();
        if (cancelled || !containerRef.current) return;

        const map = new google.maps.Map(containerRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapTypeId: 'hybrid',
          disableDefaultUI: true,
          gestureHandling: 'greedy',
        });

        const marker = new google.maps.Marker({
          map,
          position: DEFAULT_CENTER,
          draggable: true,
          title: 'Posizione sito',
        });

        mapRef.current = map;
        markerRef.current = marker;

        const updateState = () => syncStateFromMap(map, marker, stateRef);

        map.addListener('zoom_changed', updateState);
        map.addListener('center_changed', updateState);
        map.addListener('maptypeid_changed', updateState);
        marker.addListener('dragend', updateState);

        updateState();
        setIsReady(true);
        setLoadError(null);

        google.maps.event.trigger(map, 'resize');

        const pending = pendingAddressRef.current?.trim() || address?.trim();
        if (pending) {
          await goToAddressImpl(pending, false);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error instanceof Error ? error.message : 'Errore caricamento Google Maps';
          setLoadError(message);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!isReady || !address?.trim()) return;
    goToAddressImpl(address, false);
  }, [address, isReady, goToAddressImpl]);

  React.useEffect(() => {
    if (!isReady || !containerRef.current || !mapRef.current) return;

    const map = mapRef.current;
    const observer = new ResizeObserver(() => {
      google.maps.event.trigger(map, 'resize');
    });
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [isReady]);

  return (
    <Box sx={{ width: MAP_SIZE, height: MAP_SIZE, position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
      <Box ref={containerRef} sx={{ width: MAP_SIZE, height: MAP_SIZE }} />
      {loadError && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            p: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            {loadError}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

GoogleMap.displayName = 'GoogleMap';

export default GoogleMap;
